import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { SimpleIcon } from '@adthlete/common'

function TikTokCallback() {
  const location = useLocation()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const code = queryParams.get('code')
    const scopes = queryParams.get('scopes')
    const state = queryParams.get('state')
    const errorParam = queryParams.get('error')
    const errorDescription = queryParams.get('error_description')

    const handleTikTokRedirect = async () => {
      if (errorParam) {
        console.error('TikTok authorization error:', errorParam, errorDescription)
        setError(errorDescription || 'An error occurred during TikTok authorization')
        setLoading(false)
        return
      }

      if (!code) {
        setError('No authorization code received from TikTok')
        setLoading(false)
        return
      }

      try {
        // Navigate to auth steps page with the callback data
        const params = new URLSearchParams()
        if (code) params.append('code', code)
        if (scopes) params.append('scopes', scopes)
        if (state) params.append('state', state)

        navigate(`/tiktok/auth-steps?${params.toString()}`)
      } catch (err) {
        console.error('Error processing TikTok callback:', err)
        setError('An error occurred while processing your request')
        setLoading(false)
      }
    }

    handleTikTokRedirect()
  }, [location.search, navigate])

  if (loading) {
    return (
      <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-black p-4 rounded-full">
              <SimpleIcon slug="tiktok" color="white" height={32} width={32} />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Processing TikTok Authorization</h2>
          <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm text-white transition duration-150 ease-in-out">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Loading...
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-red-500 p-4 rounded-full">
              <SimpleIcon slug="tiktok" color="white" height={32} width={32} />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">TikTok Authorization Error</h2>
          <p className="text-red-400 mb-6">{error}</p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/connect/tiktok')}
              className="px-4 py-2 bg-[#C0FD72] text-black rounded-lg font-medium hover:bg-[#d2ff9c] transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="px-4 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
            >
              Back to Settings
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default TikTokCallback
